import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { _, TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { Button } from 'primeng/button';
import { FormsModule } from '@angular/forms';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { Checkbox } from 'primeng/checkbox';
import { SvgIconComponent } from 'angular-svg-icon';
import { DialogEventOverlay } from '@app/main/leak-detection/dialogs/dialog-event/enums/dialog-event-overlay.enum';
import { DialogEventHideConfService } from '@app/main/leak-detection/dialogs/dialog-event/services/dialog-event-hide-conf.service';

interface IConfirmCopy {
  icon?: string;
  title: string;
  body: string;
  cancel: string;
  confirm: string;
  destructive: boolean;
}

@Component({
  selector: 'gc-dialog-event-confirm-overlay',
  standalone: true,
  imports: [TranslateDirective, TranslatePipe, Button, FormsModule, ToggleSwitch, Checkbox, SvgIconComponent],
  templateUrl: './dialog-event-confirm-overlay.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'absolute inset-0 z-content-overlay-20 flex items-center justify-center' },
})
export class DialogEventConfirmOverlayComponent {
  private readonly hideConf = inject(DialogEventHideConfService);

  protected readonly Overlay = DialogEventOverlay;

  protected readonly dontShowAgain = signal(false);

  public overlay = input.required<DialogEventOverlay>();

  public confirmed = output<void>();

  public cancelled = output<void>();

  protected readonly copy = computed<IConfirmCopy | null>(() => {
    switch (this.overlay()) {
      case DialogEventOverlay.Discard:
        return {
          title: _(`@@fp-event-discard-prompt__title`),
          body: _(`@@fp-event-discard-prompt__body`),
          cancel: _(`@@shared-cancel__text`),
          confirm: _(`@@fp-event-discard-prompt__confirm`),
          destructive: true,
        };
      case DialogEventOverlay.Hide:
        return {
          icon: 'icons/icon-eye-hide.svg',
          title: _(`@@fp-event-hide-prompt__title`),
          body: _(`@@fp-event-hide-prompt__body`),
          cancel: _(`@@shared-cancel__text`),
          confirm: _(`@@fp-event-hide-prompt__confirm`),
          destructive: false,
        };
      default:
        return null;
    }
  });

  protected confirm(): void {
    if (this.overlay() === DialogEventOverlay.Hide && this.dontShowAgain()) this.hideConf.block();

    this.confirmed.emit();
  }

  protected cancel(): void {
    this.cancelled.emit();
  }
}
